import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Sparkles, CheckCircle2, Loader2 } from 'lucide-react';
import { publicApi } from '../api';
import { useLang } from '../i18n';

const COPY = {
  it: {
    badge: 'Iscriviti alla Newsletter',
    title: 'Il meglio delle destinazioni YUP nella tua inbox',
    desc: 'Iscriviti gratis: ricevi consigli, luoghi da scoprire e offerte esclusive dalle città YUP.',
    placeholder: 'La tua email',
    submit: 'Iscriviti',
    close: 'Chiudi',
    dismiss: 'Non ora',
    success: 'Fatto! Ci sentiamo presto.',
    error: 'Errore, riprova.',
    trust: '100% gratis · Niente spam',
  },
  en: {
    badge: 'Join the Newsletter',
    title: 'The best of YUP destinations in your inbox',
    desc: 'Sign up for free: tips, hidden places and exclusive offers from every YUP city.',
    placeholder: 'Your email',
    submit: 'Subscribe',
    close: 'Close',
    dismiss: 'Not now',
    success: "You're in! Talk soon.",
    error: 'Error, please retry.',
    trust: '100% free · No spam',
  },
  de: {
    badge: 'Newsletter abonnieren',
    title: 'Das Beste der YUP-Städte in deinem Postfach',
    desc: 'Kostenlos abonnieren: Tipps, versteckte Orte und exklusive Angebote aus allen YUP-Städten.',
    placeholder: 'Deine E-Mail',
    submit: 'Abonnieren',
    close: 'Schließen',
    dismiss: 'Nicht jetzt',
    success: 'Bestätigt! Bis bald.',
    error: 'Fehler, bitte erneut versuchen.',
    trust: '100% kostenlos · Kein Spam',
  },
  fr: {
    badge: 'Rejoindre la newsletter',
    title: 'Le meilleur des destinations YUP dans votre boîte mail',
    desc: 'Inscrivez-vous gratuitement : conseils, lieux cachés et offres exclusives des villes YUP.',
    placeholder: 'Votre email',
    submit: "S'inscrire",
    close: 'Fermer',
    dismiss: 'Plus tard',
    success: 'Inscription réussie !',
    error: 'Erreur, réessayez.',
    trust: '100% gratuit · Aucun spam',
  },
  es: {
    badge: 'Únete a la Newsletter',
    title: 'Lo mejor de los destinos YUP en tu bandeja',
    desc: 'Regístrate gratis: consejos, lugares secretos y ofertas exclusivas de las ciudades YUP.',
    placeholder: 'Tu email',
    submit: 'Suscribirme',
    close: 'Cerrar',
    dismiss: 'Ahora no',
    success: '¡Listo! Hablamos pronto.',
    error: 'Error, inténtalo de nuevo.',
    trust: '100% gratis · Sin spam',
  },
};

const STORAGE_KEY = 'yup_signup_popup_seen_v1';

/**
 * One-shot signup popup on the public shell. Rules:
 * - Only on the public site (never in /admin, /sponsor)
 * - Never shown once dismissed / submitted (localStorage flag, persistent across sessions)
 * - Triggers on FIRST scroll OR after 5s idle, whichever comes first
 */
export default function RegistrationPopup() {
  const loc = useLocation();
  const { lang } = useLang();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | ok | err
  const [errorMsg, setErrorMsg] = useState('');

  const t = COPY[lang] || COPY.en;

  // Only mount trigger listeners once, and only if the user hasn't been shown before
  useEffect(() => {
    if (loc.pathname.startsWith('/admin') || loc.pathname.startsWith('/sponsor')) return;
    let seen = false;
    try { seen = !!localStorage.getItem(STORAGE_KEY); } catch {}
    if (seen) return;
    // Don't compete with the cookie banner — wait until consent is set
    try { if (!localStorage.getItem('yup_cookie_consent_v1')) return; } catch {}

    let done = false;
    const openIt = () => {
      if (done) return;
      done = true;
      setVisible(true);
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
    };
    const onScroll = () => { if (window.scrollY > 60) openIt(); };
    const timer = setTimeout(openIt, 5000);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); clearTimeout(timer); };
  }, [loc.pathname]);

  const rememberSeen = () => { try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {} };

  const close = () => { setVisible(false); rememberSeen(); };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) { setStatus('err'); setErrorMsg(t.error); return; }
    setStatus('sending');
    try {
      await publicApi.subscribe(email, lang);
      setStatus('ok');
      rememberSeen();
      setTimeout(() => setVisible(false), 1600);
    } catch (err) {
      setStatus('err');
      setErrorMsg(err.response?.data?.detail || t.error);
    }
  };

  if (!visible) return null;

  return (
    <div className="yup-popup-backdrop" onClick={close} data-testid="signup-popup-backdrop">
      <div
        className="yup-popup-card"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="signup-popup"
      >
        <button
          onClick={close}
          className="yup-popup-close"
          aria-label={t.close}
          data-testid="signup-popup-close"
        >
          <X size={18} />
        </button>

        <div className="yup-popup-badge">
          <Sparkles size={14} /> {t.badge}
        </div>
        <h3 className="yup-popup-title">{t.title}</h3>
        <p className="yup-popup-desc">{t.desc}</p>

        {status === 'ok' ? (
          <div className="yup-popup-success" data-testid="signup-popup-success">
            <CheckCircle2 size={22} className="text-emerald-400" />
            <span>{t.success}</span>
          </div>
        ) : (
          <form onSubmit={submit} className="yup-popup-form">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={t.placeholder}
              autoComplete="email"
              className="yup-popup-input"
              data-testid="signup-popup-email"
              disabled={status === 'sending'}
            />
            <button
              type="submit"
              className="yup-popup-submit"
              data-testid="signup-popup-submit"
              disabled={status === 'sending'}
            >
              {status === 'sending' ? <Loader2 size={16} className="animate-spin" /> : null}
              {t.submit}
            </button>
          </form>
        )}
        {status === 'err' && <p className="yup-popup-error" data-testid="signup-popup-error">{errorMsg}</p>}
        <div className="yup-popup-trust">{t.trust}</div>
        <button
          onClick={close}
          className="yup-popup-dismiss"
          data-testid="signup-popup-dismiss"
        >
          {t.dismiss}
        </button>
      </div>
    </div>
  );
}
